import { useEffect, useState } from "react";

/**
 * Modal para enviar un reporte por correo.
 *
 * Solo se ocupa del formulario y de validar las direcciones antes de molestar
 * a la API. Quien lo usa recibe `{ destinatarios, asunto, mensaje }` en
 * `onEnviar` y decide qué mandar (la captura de la gráfica, los totales, etc.).
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
const MAX_DESTINATARIOS = 10;

/** Separa por coma, punto y coma, salto de línea o espacio. */
const separarCorreos = (texto) =>
  texto
    .split(/[,;\s]+/)
    .map((correo) => correo.trim().toLowerCase())
    .filter(Boolean);

const ModalEnviarCorreo = ({
  abierto,
  onCerrar,
  onEnviar,
  enviando = false,
  asuntoSugerido = "",
  resumen = null,
}) => {
  const [correos, setCorreos] = useState("");
  const [asunto, setAsunto] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  // El asunto sugerido cambia con la pregunta seleccionada: se resincroniza
  // cada vez que se abre el modal, no una sola vez al montarlo.
  useEffect(() => {
    if (!abierto) return;
    setAsunto(asuntoSugerido);
    setError("");
  }, [abierto, asuntoSugerido]);

  // Cerrar con Escape es lo que espera cualquiera frente a un modal.
  useEffect(() => {
    if (!abierto) return undefined;

    const alPresionar = (evento) => {
      if (evento.key === "Escape" && !enviando) onCerrar();
    };

    window.addEventListener("keydown", alPresionar);
    return () => window.removeEventListener("keydown", alPresionar);
  }, [abierto, enviando, onCerrar]);

  if (!abierto) return null;

  const destinatarios = separarCorreos(correos);
  const invalidos = destinatarios.filter((correo) => !EMAIL_RE.test(correo));

  const enviar = async (evento) => {
    evento.preventDefault();

    if (!destinatarios.length) {
      setError("Escribe al menos un correo destinatario.");
      return;
    }
    if (invalidos.length) {
      setError(`Estos correos no son válidos: ${invalidos.join(", ")}`);
      return;
    }
    if (destinatarios.length > MAX_DESTINATARIOS) {
      setError(`Máximo ${MAX_DESTINATARIOS} destinatarios por envío.`);
      return;
    }

    setError("");
    const ok = await onEnviar({
      destinatarios: [...new Set(destinatarios)],
      asunto: asunto.trim(),
      mensaje: mensaje.trim(),
    });

    if (ok) {
      setCorreos("");
      setMensaje("");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-modal-correo"
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget && !enviando) onCerrar();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h3 id="titulo-modal-correo" className="text-xl font-bold text-gray-900">
              Enviar reporte por correo
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Se envía la gráfica junto con el desglose de respuestas.
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            disabled={enviando}
            className="rounded-lg p-1 text-2xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
            aria-label="Cerrar"
          >
            &times;
          </button>
        </div>

        {resumen && (
          <div className="mx-6 mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            {resumen}
          </div>
        )}

        <form onSubmit={enviar} className="space-y-4 px-6 py-5">
          <div>
            <label htmlFor="correos-destino" className="block text-sm font-bold text-gray-700">
              Para <span className="text-red-500">*</span>
            </label>
            <textarea
              id="correos-destino"
              rows={2}
              value={correos}
              onChange={(evento) => setCorreos(evento.target.value)}
              placeholder="gerencia@parquemonchis.com, direccion@parquemonchis.com"
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Separa varios correos con comas. Máximo {MAX_DESTINATARIOS}.
              {destinatarios.length > 0 && (
                <span className={invalidos.length ? "ml-1 font-bold text-red-600" : "ml-1 font-bold text-emerald-600"}>
                  {destinatarios.length} detectado(s)
                  {invalidos.length > 0 && ` · ${invalidos.length} con formato inválido`}
                </span>
              )}
            </p>
          </div>

          <div>
            <label htmlFor="asunto-correo" className="block text-sm font-bold text-gray-700">
              Asunto
            </label>
            <input
              id="asunto-correo"
              type="text"
              value={asunto}
              onChange={(evento) => setAsunto(evento.target.value)}
              maxLength={200}
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="mensaje-correo" className="block text-sm font-bold text-gray-700">
              Mensaje <span className="font-normal text-gray-400">(opcional)</span>
            </label>
            <textarea
              id="mensaje-correo"
              rows={3}
              value={mensaje}
              onChange={(evento) => setMensaje(evento.target.value)}
              maxLength={2000}
              placeholder="Nota para quien recibe el reporte…"
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert">
              {error}
            </p>
          )}

          <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCerrar}
              disabled={enviando}
              className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={enviando}
              className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              {enviando ? "Enviando…" : "Enviar reporte"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEnviarCorreo;
